import { IdentityValidationDocumentName } from '@contexts/IdentityValidation/domain';
import { AptunoErrorType } from '@shared/Core';
import { axiosClient } from '@shared/data-access';
import { AptunoError } from '@shared/models';
import { useEffect, useState } from 'react';
import { useDocumentActions } from '../store/documents.actions.context';
import { LoadingStatus } from './loading-status';

type UploadFileData = {
  name: IdentityValidationDocumentName;
  identityValidationId: string;
  file: File;
};

export const UPLOAD_FILE_IN_EXTERNAL_ERROR_CODE = 'upload-file-in-external-error';
export const MAX_LOAD_RETRIES = 2;

export const useUploadFileInexternal = () => {
  const {
    getSignedUrl,
    signedUrl,
    loading: loadingGetSignedUrl,
    recall: refetchSignedUrl,
    error: getSignedUrlError,
  } = useDocumentActions().documentQueryActions.useGetSignedUrl();

  const [uploadFileData, setUploadFileData] = useState<UploadFileData>();
  const [uploadFileErrors, setUploadFileErros] = useState<AptunoError[]>([]);
  const [uploadingToUrl, setUploadingToUrl] = useState<LoadingStatus>(LoadingStatus.NOT_INITIALIZED);
  const [loadRetries, setLoadRetries] = useState(0);

  useEffect(() => {
    if (uploadFileData) {
      if (signedUrl) {
        refetchSignedUrl();
      } else {
        getSignedUrl({
          identityValidationId: uploadFileData.identityValidationId,
          document: uploadFileData.name,
        });
      }
    }
  }, [uploadFileData]);

  useEffect(() => {
    if (signedUrl && uploadFileData?.file) {
      uploadFile(uploadFileData.file, signedUrl);
    }
  }, [signedUrl]);

  useEffect(() => {
    if (getSignedUrlError) {
      setUploadFileErros((prev) => [...prev, getSignedUrlError]);
    }
  }, [getSignedUrlError]);

  const uploadFile = (file: File, url: string) => {
    setUploadingToUrl(() => LoadingStatus.LOADING);
    axiosClient
      .put(url, file, {
        headers: {
          'Content-Type': file.type,
        },
      })
      .then(() => setUploadingToUrl(() => LoadingStatus.LOADED))
      .catch((error: Error) => {
        if (loadRetries < MAX_LOAD_RETRIES) {
          console.log(`Init retrying file uploading: file [${file.name}] retry number [${loadRetries + 1}]`);

          setLoadRetries((prev) => prev + 1);
          refetchSignedUrl();
        } else {
          setUploadFileErros((prev) => [
            ...prev,
            <AptunoError>{
              type: AptunoErrorType.TECHNICAL,
              code: UPLOAD_FILE_IN_EXTERNAL_ERROR_CODE,
              description: `An error has occurred loading the file [${file.name}] in external url [${url}]. Error: ${error.message}`,
              params: [uploadFileData?.file.name, signedUrl],
            },
          ]);
          setUploadingToUrl(() => LoadingStatus.ERROR);
        }
      });
  };

  return {
    uploadFileInExternal: (data: UploadFileData) => {
      setLoadRetries(0);
      setUploadFileData(() => data);
    },
    loading: uploadingToUrl === LoadingStatus.LOADING || loadingGetSignedUrl,
    loaded:
      uploadingToUrl === LoadingStatus.LOADED &&
      !loadingGetSignedUrl &&
      uploadFileErrors.length === 0 &&
      uploadFileData,
    errors: uploadFileErrors,
  };
};
